import React from "react";
import Immutable from "immutable";

import useDimensions from "js/common/utils/use-dimensions";
import * as Users from "js/common/users";
import * as Groups from "js/common/groups";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as Branding from "js/common/branding-constants";
import * as DashboardUtils from "js/dashboards/utils";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {stripEmoji} from "js/common/utils/strings";
import {TextButton} from "js/common/views/inputs/buttons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUser, faClock} from "@fortawesome/pro-regular-svg-icons";
import PreviewTooltip from "js/oneview/header/tooltip";
import {PreviewTooltipContent} from "js/common/views/filter-drawer/filter-button";
import AiAssistantButton from "js/dashboards/toolbar/ai-assistant-button";
import currentClient from "js/common/repo/backbone/current-client";
import {clientPermissions} from "js/common/permissions";

const DashboardToolbar = React.memo(({
      dashboard,
      isEditing,

      onRequestOpenAiAssistant,
      onRequestOpenComponentMenu,
      onRequestReloadDashboard,
      handleNameChange,

      filtersExpanded,
      onToggleFilters,
      filterConfig,
      children
    }) => {
      const {isFullEditor} = DashboardUtils.useEditorFlags();

      const {theme} = React.useContext(CustomThemeContext);

      const [dimRef, dimensions] = useDimensions();
      const {width} = dimensions;
      const smallResolution = width <= 1024;

      const hasAiAssistant = currentClient.hasPermission(clientPermissions.canUseAiAssistant);

      const isAddPanelDisabled = dashboard?.getIn(["json", "layoutType"]) === "window-only"
          && dashboard?.getIn(["json", "components"], Immutable.List()).size >= 1;

      return <div
          ref={dimRef}
          style={{
            backgroundColor: theme.palette.background.card,
            boxShadow: "rgb(0 0 0 / 20%) 0px 2px 1px -1px",
            padding: "0.8rem 1.5rem",
            position: "relative",
            zIndex: 101
          }}>
        <div style={{display: "flex", alignItems: "center"}}>
          <div style={{flex: 1, display: "flex", alignItems: "center"}}>
            {dashboard && (
                isEditing
                    ? <input
                        value={dashboard.get("name")}
                        data-test-id="edit-dashboard-name"
                        onChange={e => handleNameChange(stripEmoji(e.target.value).substring(0, 255))}
                        style={{
                          fontSize: "1em",
                          fontFamily: "inherit",
                          fontWeight: "inherit",
                          border: "none",
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          outline: "none",
                          background: "transparent",
                          color: theme.palette.text.main,
                          width: 500,
                          padding: "0 4px",
                          lineHeight: "inherit"
                        }} />
                    : <h3 style={{margin: 0, fontSize: "1em"}}>{dashboard.get("name")}</h3>
            )}
          </div>

          <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem"}}>
            {hasAiAssistant && (
                <AiAssistantButton
                    className="hide-for-print"
                    label={smallResolution ? "" : Branding.aiAssistantName}
                    onClick={onRequestOpenAiAssistant} />
            )}
            {(isFullEditor && isEditing) && (
                <TextButton
                    className="hide-for-print"
                    dataProductId="add-panel-button"
                    testId="add-panel-button"
                    style={{fontSize: "12px", whiteSpace: "nowrap", paddingRight: 15, paddingLeft: 15}}
                    disabled={isAddPanelDisabled}
                    type="inverted"
                    label={smallResolution ? "" : "Add Panel"}
                    iconType="bhi"
                    icon="addcard"
                    onClick={onRequestOpenComponentMenu} />
            )}
            <FilterToggleButton
                filtersExpanded={filtersExpanded}
                onToggleFilters={onToggleFilters}
                filterConfig={filterConfig} />
            <TextButton
                className="hide-for-print"
                testId="refresh-dashboard-button"
                icon="refresh" iconType="bhi" label="" onClick={onRequestReloadDashboard}
                style={{fontSize: "12px", padding: 0, minWidth: 50}} />
          </div>
        </div>
        {children}
      </div>;
    })
;

const FilterToggleButton = React.memo(({filtersExpanded, onToggleFilters, filterConfig}) => {
  const [filterNames, setFilterNames] = React.useState(Immutable.Map());
  const {theme} = React.useContext(CustomThemeContext);

  const clientIds = filterConfig
      ? filterConfig.getIn(["clientFilter", "clientIds"], Immutable.Set())
      : Immutable.Set();
  const clientSetIds = filterConfig
      ? filterConfig.getIn(["clientFilter", "clientSetIds"], Immutable.Set())
      : Immutable.Set();
  const matchAnyTagIds = filterConfig
      ? filterConfig.getIn(["tagFilter", "matchAnyTagIds"], Immutable.Set())
      : Immutable.Set();
  const matchAllTagIds = filterConfig
      ? filterConfig.getIn(["tagFilter", "matchAllTagIds"], Immutable.Set())
      : Immutable.Set();
  const excludedTagIds = filterConfig
      ? filterConfig.getIn(["tagFilter", "excludedTagIds"], Immutable.Set())
      : Immutable.Set();

  const hasActiveFilters = !clientIds.isEmpty() || !clientSetIds.isEmpty()
      || !matchAnyTagIds.isEmpty() || !matchAllTagIds.isEmpty() || !excludedTagIds.isEmpty();

  const activeFilterStyle = hasActiveFilters
      ? theme.themeId === "light"
          ? {background: "#4a89dc40", color: theme.palette.primary.main}
          : {background: "rgba(255,255,255,0.12)", color: theme.palette.primary.light}
      : {};

  const activeStyle = {...activeFilterStyle};

  const qualifierDisplay = React.useMemo(() => {
    if (!filterConfig) return "All Users";
    const qualifier = filterConfig.get("qualifier", Immutable.Map());
    const type = qualifier.get("type");
    const id = qualifier.get("id");
    if (!type || id === undefined || id === null) return "All Users";
    if (type === "USER") {
      if (id === 0) return "Logged in User";
      const user = Users.getUser(id);
      return user ? user.get("firstName") + " " + user.get("lastName") : "User #" + id;
    } else if (type === "GROUP") {
      if (id === 0) return "Logged in User's Group";
      const group = Groups.getGroup(id);
      return group ? group.get("name") : "Group #" + id;
    }
    return "Unknown";
  }, [filterConfig]);

  const timeframeDisplay = React.useMemo(() => {
    if (!filterConfig) return "N/A";
    const timeframe = filterConfig.get("timeframe", Immutable.Map());
    const timeframeObj = timeframe.toJS ? timeframe.toJS() : timeframe;
    if (!timeframeObj || !timeframeObj.type) return "N/A";
    const parsed = TimeframeRepo.parse(timeframeObj);
    if (!parsed) return "N/A";
    const name = parsed.get("name");
    const start = parsed.get("start");
    const end = parsed.get("end");
    let dateRangeStr = "";
    if (start && end) {
      dateRangeStr = ` (${start.format("D MMM YYYY")} to ${end.format("D MMM YYYY")})`;
    }
    if (name) return name + dateRangeStr;
    if (dateRangeStr) return dateRangeStr.substring(2, dateRangeStr.length - 1);
    return "N/A";
  }, [filterConfig]);

  const tooltipContent = <div>
    <div style={{paddingBottom: "10px", paddingTop: "5px"}}>
      <div style={{paddingBottom: "5px"}}>
        <FontAwesomeIcon icon={faUser} size="lg" style={{paddingRight: "5px"}} />
        <b>User / Group</b>
      </div>
      <div style={{padding: "4px 10px", fontSize: "0.75rem"}}>{qualifierDisplay}</div>
    </div>
    <div style={{paddingBottom: "10px", paddingTop: "5px"}}>
      <div style={{paddingBottom: "5px"}}>
        <FontAwesomeIcon icon={faClock} size="lg" style={{paddingRight: "5px"}} />
        <b>Timeframe</b>
      </div>
      <div style={{padding: "4px 10px", fontSize: "0.75rem"}}>{timeframeDisplay}</div>
    </div>
    <PreviewTooltipContent
        filterNames={filterNames}
        matchAllTagIds={matchAllTagIds}
        matchAnyTagIds={matchAnyTagIds}
        excludedTagIds={excludedTagIds}
        clientIds={clientIds}
        clientSetIds={clientSetIds} />
  </div>;

  const button = <TextButton
      className="hide-for-print"
      testId="toggle-filters-button"
      icon="filter"
      iconType="bhi"
      label=""
      onClick={onToggleFilters}
      style={{fontSize: "12px", padding: 0, minWidth: 50, ...activeStyle}} />;

  if (filtersExpanded) {
    return button;
  }

  return <PreviewTooltip
      hoverDelay={200}
      rightAlign
      tooltipOffset={0}
      arrowOffset="calc(100% - 25px)"
      typeToIdToNamedItem={filterNames}
      setNames={setFilterNames}
      content={tooltipContent}
      selectedClientIds={clientIds}
      selectedClientSetIds={clientSetIds}
      customStyle={{display: "inline-flex", cursor: "pointer"}}>
    {button}
  </PreviewTooltip>;
});

export default DashboardToolbar;
